'use client';

import React from 'react';
import { Eye, Trash2, CheckSquare, Square, TrendingUp, TrendingDown, Calendar, Clock } from 'lucide-react';
import { BacktestRun } from '../utils/backtestStorage';

interface BacktestRunCardProps {
  run: BacktestRun;
  selected: boolean;
  onSelect: () => void;
  onView: () => void;
  onDelete: () => void;
  selectionDisabled: boolean;
}

export default function BacktestRunCard({ run, selected, onSelect, onView, onDelete, selectionDisabled }: BacktestRunCardProps) {
  const { params, results } = run;
  const isProfit = results.netReturn >= 0;

  return (
    <div
      className={`relative bg-card border rounded-xl overflow-hidden transition-all duration-200 group ${
        selected
          ? 'border-primary/60 ring-1 ring-primary/30 card-glow-primary' :'border-border hover:border-primary/30'
      }`}
    >
      {/* Selection checkbox */}
      <button
        onClick={onSelect}
        disabled={selectionDisabled}
        className={`absolute top-3 right-3 z-10 transition-all ${
          selectionDisabled && !selected ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
        }`}
        title={selected ? 'Deselect' : 'Select to compare'}
      >
        {selected ? (
          <CheckSquare size={16} className="text-primary" />
        ) : (
          <Square size={16} className="text-muted-foreground hover:text-foreground" />
        )}
      </button>

      {/* Card header */}
      <div className="px-4 pt-4 pb-3 border-b border-border/60">
        <div className="flex items-start gap-3 pr-6">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isProfit ? 'bg-positive/10' : 'bg-negative/10'
          }`}>
            {isProfit ? (
              <TrendingUp size={16} className="text-positive" />
            ) : (
              <TrendingDown size={16} className="text-negative" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-600 text-foreground truncate">{run.name}</h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs font-mono-nums font-600 text-primary">{params.symbol}</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{params.timeframe}</span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground">{params.startDate} → {params.endDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-px bg-border/40 border-b border-border/60">
        <KpiCell label="Win Rate" value={`${results.winRate}%`} positive={results.winRate >= 50} />
        <KpiCell label="Net Return" value={`${isProfit ? '+' : ''}$${results.netReturn.toLocaleString()}`} positive={isProfit} />
        <KpiCell label="Profit Factor" value={results.profitFactor.toFixed(2)} positive={results.profitFactor >= 1.5} />
        <KpiCell label="Max Drawdown" value={`${results.maxDrawdownPct.toFixed(1)}%`} positive={false} warning />
      </div>

      {/* Mini equity sparkline */}
      <div className="px-4 py-3 border-b border-border/60">
        <p className="text-xs text-muted-foreground mb-2">Equity Curve</p>
        <MiniSparkline data={results.equityCurve} positive={isProfit} />
      </div>

      {/* Footer */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {new Date(run.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={11} />
            {results.totalTrades} trades
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onView}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            title="View details"
          >
            <Eye size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-negative hover:bg-negative/10 transition-colors"
            title="Delete run"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function KpiCell({ label, value, positive, warning }: { label: string; value: string; positive: boolean; warning?: boolean }) {
  return (
    <div className="bg-card px-3 py-2.5">
      <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
      <p className={`text-sm font-600 font-mono-nums ${
        warning ? 'text-warning' : positive ? 'text-positive' : 'text-negative'
      }`}>
        {value}
      </p>
    </div>
  );
}

function MiniSparkline({ data, positive }: { data: { month: string; equity: number }[]; positive: boolean }) {
  if (!data || data.length < 2) return null;
  const values = data.map((d) => d.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 100;
  const h = 32;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  });
  const polyline = pts.join(' ');
  const fill = `${pts.join(' ')} ${w},${h} 0,${h}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`spark-grad-${positive}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={positive ? 'var(--positive)' : 'var(--negative)'} stopOpacity="0.3" />
          <stop offset="100%" stopColor={positive ? 'var(--positive)' : 'var(--negative)'} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fill} fill={`url(#spark-grad-${positive})`} />
      <polyline points={polyline} fill="none" stroke={positive ? 'var(--positive)' : 'var(--negative)'} strokeWidth="1.5" />
    </svg>
  );
}
